import { getOrganizationAvatar } from "@frontend-shared/utils";
import { memo } from "react";

import { cn } from "@/lib/utils";

interface IOrganizationAvatarProps {
  name: string;
  uuid?: string;
  className?: string;
}

/**
 * NOTE: NSAI-6258 — an organization's badge: its initials on a colour picked
 * from the shared palette by a hash of its uuid, so the same organization wears
 * the same colour on web and on mobile, on every device and after a rename.
 *
 * Organizations have no logo to show — nothing in the product uploads one — so
 * what stands in for it has to come from what an organization already has. A
 * coloured disc with two letters is the compact form the design asks for: it
 * survives at 20px in a dropdown row, and it is the colour, not the text, that
 * does the telling-apart when the list is being scanned rather than read.
 *
 * The fill is an inline style rather than a class because the colour is data,
 * not a design token: it is chosen per organization at runtime, and Tailwind
 * only emits classes it can see at build time.
 */
const OrganizationAvatarComponent = ({
  name,
  uuid,
  className,
}: IOrganizationAvatarProps): JSX.Element => {
  const { initials, color } = getOrganizationAvatar({ name, uuid });

  return (
    <span
      // NOTE: the name is already beside every badge that has one, so the badge
      // itself is decoration to a screen reader rather than a second copy of
      // the label.
      aria-hidden="true"
      data-testid="organization-avatar"
      data-organization-initials={initials}
      className={cn(
        "flex h-7 w-7 shrink-0 select-none items-center justify-center rounded-full",
        "text-[11px] font-semibold uppercase leading-none tracking-wide text-white",
        className,
      )}
      style={{ backgroundColor: color }}
    >
      {initials}
    </span>
  );
};

export const OrganizationAvatar = memo(OrganizationAvatarComponent);
